import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../axiosInstance";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

interface AuthState {
  loading: boolean;
  error: string | null;
  token: string | null;
  business_type: string | null;
  valid: boolean;
  message: string | null;
}

const initialState: AuthState = {
  loading: false,
  error: null,
  token: null,
  business_type: null,
  valid: false,
  message: null,
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    { email, password }: { email: string; password: string },
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.post("/auth", { email, password });

      const token = response.data.data.token;
      const business_type = response.data.data.business_type;
      const message = response.data.message;
      const valid = response.data.valid;

      localStorage.setItem("authToken", token);

      return { token, business_type, valid, message };
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Something went wrong, please try again."
      );
    }
  }
);

const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "business_type", "valid", "message"],
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    logout(state) {
      Object.assign(state, initialState);
      [
        "authToken",
        "verify_status",
        "verify_username",
        "verify_id",
        "persist:root",
      ].forEach((key) => localStorage.removeItem(key));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            token: string;
            business_type: string;
            valid: boolean;
            message: string;
          }>
        ) => {
          state.loading = false;
          state.token = action.payload.token;
          state.business_type = action.payload.business_type;
          state.valid = action.payload.valid;
          state.message = action.payload.message;
        }
      )
      .addCase(loginUser.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, logout } = authSlice.actions;

// Export persisted reducer
export default persistReducer(authPersistConfig, authSlice.reducer);
